import { Checkbox, DatePicker, Layout, Tag, Typography } from 'antd';
import { Dayjs } from 'dayjs';
import { Interval } from 'luxon';

import {
    FacilityDto,
    NurseProfileDto,
    ShiftListItemDto,
    ShiftStatus,
    ShiftTime,
    ShiftType,
} from '@clh/api-client';
import {
    CostCalculator,
    calculateTotalBonus,
    calculateTotalStipend,
    getEstimatedMinutesToBeWorkedFromShiftLength,
    getShiftDuration,
    roundToAtMostTwoDecimals,
    toDollarAmount,
    toHourAndMinuteDisplay,
} from '@clh/util';

import { getDateStrWithTimezone } from '../helpers';
import { useApiClient } from '../hooks/use-api-client';
import { Content, Header } from '../layout';
import Spinner from '../spinner/spinner';
import { DrilldownTable, FilterSelect } from '../table';

import { SHIFT_TYPE_LABEL } from './shift-type-label';
import { Tabs } from './tabs';

const Text = Typography.Text;

const SHIFT_TYPE_OPTIONS = Object.entries(SHIFT_TYPE_LABEL).map(
    ([value, label]) => ({ value: value as ShiftType, label })
);

export default function Shifts() {
    const api = useApiClient();

    if (!api) {
        return <Spinner />;
    }

    return (
        <Layout>
            <Header>
                <h2>Shifts</h2>
            </Header>
            <Content>
                <DrilldownTable
                    filters={{
                        search: 'search',
                        searchPlaceholder:
                            'Enter nurse name, facility name, shift ID, or other…',
                        filters: {
                            assigned: {
                                label: 'Unassigned',
                                noToggleButton: true,
                                control: ({ onApply, params }) => {
                                    return (
                                        <Checkbox
                                            checked={params?.assigned === false}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    onApply(false); //
                                                } else {
                                                    onApply(undefined);
                                                }
                                            }}
                                        >
                                            Unassigned
                                        </Checkbox>
                                    );
                                },
                            },
                            shiftType: {
                                label: 'Shift Type',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="Shift Type"
                                        options={SHIFT_TYPE_OPTIONS}
                                    />
                                ),
                            },
                            status: {
                                label: 'Status',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="Status"
                                        options={[
                                            {
                                                label: 'Active',
                                                value: ShiftStatus.Active,
                                            },
                                            {
                                                label: 'Canceled',
                                                value: ShiftStatus.Canceled,
                                            },
                                        ]}
                                    />
                                ),
                            },
                            shiftDate: {
                                label: 'Shift Date',
                                paramKeys: ['after', 'before'],
                                control: ({ setFilter, onApply }) => (
                                    <DatePicker.RangePicker
                                        onChange={(value) => {
                                            if (value?.length === 2) {
                                                if (
                                                    value[0]?.toISOString() ===
                                                    value[1]?.toISOString()
                                                ) {
                                                    onApply(value[0]);
                                                } else {
                                                    setFilter(
                                                        {
                                                            after: value[0],
                                                            before: value[1],
                                                        },
                                                        value
                                                    );
                                                }
                                            }
                                        }}
                                    />
                                ),
                                onReset: (setFilter) => {
                                    setFilter({
                                        after: undefined,
                                        before: undefined,
                                        shiftDate: undefined,
                                    });
                                },
                                displayValue: (
                                    value: Dayjs | [Dayjs, Dayjs]
                                ) => {
                                    if (Array.isArray(value)) {
                                        const day1 = value[0]
                                            .toDate()
                                            .toLocaleDateString();
                                        const day2 = value[1]
                                            .toDate()
                                            .toLocaleDateString();

                                        if (day1 === day2) {
                                            return day1;
                                        }

                                        return `${day1} - ${day2}`;
                                    }

                                    return value.toDate().toLocaleDateString();
                                },
                            },
                        },
                    }}
                    getRecordById={async (id) => {
                        const shifts = await api.shiftControllerListShifts({
                            search: id,
                        });

                        return shifts.result[0];
                    }}
                    getRecords={(params?: any) => {
                        return api.shiftControllerListShifts(params);
                    }}
                    paginated
                    cacheKey="shiftControllerListShifts"
                    columns={[
                        {
                            dataIndex: 'id',
                            title: 'ID',
                            render: (value: string) => (
                                <Typography.Text code>
                                    {value.slice(0, 8)}
                                </Typography.Text>
                            ),
                        },
                        {
                            dataIndex: 'startTime',
                            title: 'Start Time',
                            render: (
                                startTime: Date,
                                shift: ShiftListItemDto
                            ) =>
                                getDateStrWithTimezone(
                                    startTime,
                                    shift.facility.address.timezone
                                ),
                            sorter: true,
                        },
                        {
                            dataIndex: 'endTime',
                            title: 'End Time',
                            render: (endTime: Date, shift: ShiftListItemDto) =>
                                getDateStrWithTimezone(
                                    endTime,
                                    shift.facility.address.timezone
                                ),
                        },
                        {
                            dataIndex: 'facility',
                            title: 'Facility',
                            render: (facility: FacilityDto) => facility.name,
                            sorter: true,
                        },
                        'shiftType',
                        {
                            dataIndex: 'specialtyCode',
                            title: 'Specialty',
                            sorter: true,
                        },
                        {
                            dataIndex: 'status',
                            title: 'Status',
                            render: (status: ShiftStatus) => (
                                <Tag
                                    color={
                                        status === ShiftStatus.Canceled
                                            ? 'error'
                                            : 'success'
                                    }
                                >
                                    {status}
                                </Tag>
                            ),
                            sorter: true,
                        },
                        {
                            dataIndex: 'shiftTime',
                            title: 'Shift Time',
                            render: (
                                shiftTime: ShiftTime,
                                shift: ShiftListItemDto
                            ) => {
                                const scheduledMinutes =
                                    shiftTime === ShiftTime.Custom
                                        ? getEstimatedMinutesToBeWorkedFromShiftLength(
                                              Interval.fromDateTimes(
                                                  shift.startTime,
                                                  shift.endTime
                                              ).toDuration('minutes').minutes
                                          )
                                        : getShiftDuration(shift.shiftTime) *
                                          60;

                                return (
                                    <>
                                        {shiftTime}
                                        <small>
                                            <br />
                                            {toHourAndMinuteDisplay(
                                                scheduledMinutes,
                                                true
                                            )}{' '}
                                            scheduled
                                        </small>
                                    </>
                                );
                            },
                        },
                        {
                            dataIndex: 'totalRate',
                            title: 'Cost',
                            sorter: true,
                            render: (
                                totalRate: number,
                                shift: ShiftListItemDto
                            ) => {
                                if (
                                    shift.shiftType === ShiftType.OnCall &&
                                    shift.onCallShift
                                ) {
                                    if (shift.minutesWorked === null) {
                                        return (
                                            <>
                                                {toDollarAmount(
                                                    shift.onCallShift.totalRate
                                                )}
                                                /hr On-Call
                                                <br />
                                                {toDollarAmount(totalRate)}/hr
                                                called-back
                                            </>
                                        );
                                    }

                                    const onCallHours =
                                        (shift.onCallShift.minutesWorked || 0) /
                                        60;
                                    const calledInHours =
                                        (shift.minutesWorked || 0) / 60;
                                    const onCallCost =
                                        shift.onCallShift.totalRate *
                                        onCallHours;
                                    const calledInCost =
                                        totalRate * calledInHours;
                                    const stipendCost = calculateTotalStipend(
                                        shift.stipends?.filter(
                                            (s) => s.qualified
                                        )
                                    );
                                    const totalCost =
                                        onCallCost + calledInCost + stipendCost;

                                    return (
                                        <>
                                            {toDollarAmount(totalCost)}
                                            <small>
                                                <br />
                                                {roundToAtMostTwoDecimals(
                                                    onCallHours
                                                )}
                                                hrs &times;{' '}
                                                {toDollarAmount(
                                                    shift.onCallShift.totalRate
                                                )}{' '}
                                                On-Call
                                                {calledInHours > 0 && (
                                                    <>
                                                        <br />
                                                        {roundToAtMostTwoDecimals(
                                                            calledInHours
                                                        )}
                                                        hrs &times;{' '}
                                                        {toDollarAmount(
                                                            totalRate
                                                        )}{' '}
                                                        called-back
                                                    </>
                                                )}
                                                {stipendCost > 0 && (
                                                    <>
                                                        {' + '}
                                                        {toDollarAmount(
                                                            stipendCost
                                                        )}{' '}
                                                        stipend
                                                    </>
                                                )}
                                            </small>
                                        </>
                                    );
                                }

                                // For non-On-Call shifts (existing logic)
                                const duration =
                                    shift.shiftTime === ShiftTime.Custom
                                        ? getEstimatedMinutesToBeWorkedFromShiftLength(
                                              Interval.fromDateTimes(
                                                  shift.startTime,
                                                  shift.endTime
                                              ).toDuration('minutes').minutes
                                          )
                                        : getShiftDuration(shift.shiftTime) *
                                          60;

                                const costs = CostCalculator.getTotalCosts({
                                    inputRate: totalRate,
                                    platformFeePct: shift.platformFeePct,
                                    employerFeePct: shift.employerFeePct,
                                    minutesWorked: duration,
                                    stipend: calculateTotalStipend(
                                        shift.stipends?.filter(
                                            (s) => s.qualified
                                        )
                                    ),
                                    bonus: calculateTotalBonus(shift.bonuses),
                                });

                                return (
                                    <>
                                        {toDollarAmount(costs.total)}
                                        <small>
                                            <br />
                                            {toDollarAmount(totalRate)} &times;{' '}
                                            {roundToAtMostTwoDecimals(
                                                duration / 60
                                            )}
                                            hrs
                                            {costs.stipend > 0 && (
                                                <>
                                                    {' + '}
                                                    {toDollarAmount(
                                                        costs.stipend
                                                    )}{' '}
                                                    stipend
                                                </>
                                            )}
                                        </small>
                                    </>
                                );
                            },
                        },
                        {
                            dataIndex: 'nurseProfile',
                            title: 'Assigned Nurse',
                            sorter: true,
                            render: (
                                nurseProfile: NurseProfileDto | null,
                                shift: ShiftListItemDto
                            ) => {
                                const totalMinutesWorked =
                                    (shift.minutesWorked || 0) +
                                    (shift.onCallShift?.minutesWorked || 0);

                                return (
                                    <>
                                        {nurseProfile ? (
                                            <>
                                                {nurseProfile.firstName}{' '}
                                                {nurseProfile.lastName}
                                                <small>
                                                    <br />

                                                    {!totalMinutesWorked ? (
                                                        shift.startTime >
                                                        new Date() ? (
                                                            <>
                                                                Scheduled to
                                                                work
                                                            </>
                                                        ) : shift.endTime <
                                                          new Date() ? (
                                                            <>
                                                                Awaiting
                                                                timecard
                                                            </>
                                                        ) : (
                                                            <>
                                                                Currently
                                                                working
                                                            </>
                                                        )
                                                    ) : (
                                                        <>
                                                            Time submitted:{' '}
                                                            {toHourAndMinuteDisplay(
                                                                totalMinutesWorked,
                                                                true
                                                            )}
                                                        </>
                                                    )}
                                                </small>
                                            </>
                                        ) : (
                                            ''
                                        )}
                                        {shift.cancellations
                                            ? shift.cancellations.map(
                                                  (cancellation, ix) => (
                                                      <Text
                                                          type="danger"
                                                          key={`${cancellation.shiftId}-${ix}`}
                                                          delete={true}
                                                      >
                                                          {
                                                              cancellation
                                                                  .nurseProfile
                                                                  .firstName
                                                          }{' '}
                                                          {
                                                              cancellation
                                                                  .nurseProfile
                                                                  .lastName
                                                          }
                                                          <br />
                                                      </Text>
                                                  )
                                              )
                                            : null}
                                    </>
                                );
                            },
                        },
                    ]}
                >
                    <Tabs />
                </DrilldownTable>
            </Content>
        </Layout>
    );
}
